import { API_PREFIX } from '@guanwei/shared';

/**
 * 统一的 API 客户端封装
 *
 * 设计：
 *   - 自动注入 Authorization: Bearer <token>
 *   - 401 时清空 token（避免无限重试）
 *   - 业务错误抛出 ApiError，UI 层 catch 显示 message
 *   - 支持 SSE 流式调用
 */

const TOKEN_KEY = 'guanwei.token';

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export interface ApiOk<T> {
  success: true;
  message?: string;
  data: T;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** 是否要求登录（默认 false，不附带 token） */
  auth?: boolean;
  signal?: AbortSignal;
}

const BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? API_PREFIX;

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (opts.auth ?? true) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? 'POST',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  let payload: unknown = null;
  try {
    payload = await resp.json();
  } catch {
    /* 非 JSON 响应 */
  }

  if (!resp.ok) {
    if (resp.status === 401) setToken(null);
    const msg = (payload as { message?: string } | null)?.message ?? `请求失败 (${resp.status})`;
    const code = (payload as { code?: string } | null)?.code;
    throw new ApiError(msg, resp.status, code);
  }

  // 兼容两种返回格式：{ success, data, message } 与 直接 data
  if (payload && typeof payload === 'object' && 'success' in payload) {
    const wrapped = payload as { success: boolean; data?: T; message?: string };
    if (wrapped.success === false) {
      throw new ApiError(wrapped.message ?? '业务错误', resp.status);
    }
    return wrapped.data as T;
  }
  return payload as T;
}

export const api = {
  get<T>(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return request<T>(path, { ...opts, method: 'GET' });
  },
  post<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return request<T>(path, { ...opts, method: 'POST', body });
  },
  put<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return request<T>(path, { ...opts, method: 'PUT', body });
  },
  del<T>(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return request<T>(path, { ...opts, method: 'DELETE' });
  },
};

/**
 * SSE 流式请求
 *
 * 用 fetch + ReadableStream 解析（EventSource 不支持自定义 headers/POST body）
 */
export async function postStream(
  path: string,
  body: unknown,
  handlers: {
    onEvent: (event: string, data: unknown) => void;
    onError?: (err: Error) => void;
    onDone?: () => void;
  },
  opts: { auth?: boolean; signal?: AbortSignal } = {},
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (opts.auth ?? true) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const resp = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: opts.signal,
  });

  if (!resp.ok || !resp.body) {
    if (resp.status === 401) setToken(null);
    const text = await resp.text().catch(() => '');
    let message = text || `SSE 失败 (${resp.status})`;
    let code: string | undefined;
    try {
      const parsed = JSON.parse(text) as { message?: string | string[]; code?: string };
      if (Array.isArray(parsed.message)) message = parsed.message.join('；');
      else if (parsed.message) message = parsed.message;
      code = parsed.code;
    } catch {
      /* ignore */
    }
    handlers.onError?.(new ApiError(message, resp.status, code));
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';
  let completedBySse = false;
  let failedBySse = false;

  const isAbortLikeError = (err: unknown) => {
    if (!(err instanceof Error)) return false;
    return (
      err.name === 'AbortError' ||
      err.message.includes('aborted') ||
      err.message.includes('BodyStreamBuffer was aborted')
    );
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // 按 SSE 协议：以空行（\n\n 或 \r\n\r\n）分隔事件
      let sep = buffer.search(/\r?\n\r?\n/);
      while (sep !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + (buffer[sep] === '\r' ? 4 : 2));
        for (const line of block.split(/\r?\n/)) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            const eventName = currentEvent;
            const payload = line.slice(5).trim();
            if (eventName === 'done') completedBySse = true;
            if (eventName === 'error') failedBySse = true;
            try {
              handlers.onEvent(eventName, JSON.parse(payload));
            } catch {
              handlers.onEvent(eventName, payload);
            }
            currentEvent = 'message';
          }
        }
        sep = buffer.search(/\r?\n\r?\n/);
      }
    }
    if (!completedBySse && !failedBySse) handlers.onDone?.();
  } catch (err) {
    if (completedBySse && isAbortLikeError(err)) return;
    handlers.onError?.(err as Error);
  } finally {
    reader.releaseLock();
  }
}
